import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const EmailCustomerCard = ({ itemId }) => {
  const [templates, setTemplates] = useState([]);
  const [templateKey, setTemplateKey] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open && templates.length === 0) {
      api.get('/team/email-templates').then(r => setTemplates(r.data || [])).catch(() => {});
    }
  }, [open, templates.length]);

  const pickTemplate = (key) => {
    setTemplateKey(key);
    const t = templates.find(x => x.template_key === key);
    if (t) {
      setSubject(t.subject || '');
      // Stored templates are fully-wrapped HTML documents; extract just the
      // editable content so the send path can re-wrap it with a single header.
      const raw = t.body_html || '';
      try {
        const doc = new DOMParser().parseFromString(raw, 'text/html');
        const content = doc.querySelector('.content');
        setBody(content ? content.innerHTML.trim() : raw);
      } catch {
        setBody(raw);
      }
    }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and message are required'); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('subject', subject);
      fd.append('body_html', body);
      fd.append('template_key', templateKey || 'staff_custom');
      if (attachment) fd.append('attachment', attachment);
      await api.post(`/team/items/${itemId}/email-customer`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Email sent to customer');
      setSubject(''); setBody(''); setTemplateKey(''); setAttachment(null); setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally { setSending(false); }
  };

  return (
    <Card className="bg-white border-stone/50">
      <CardContent className="p-4">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Mail className="h-3 w-3" /> Email Customer</h3>
        {!open ? (
          <Button size="sm" variant="outline" className="w-full text-xs gap-1.5" onClick={() => setOpen(true)} data-testid="email-customer-btn">
            <Mail className="h-3.5 w-3.5" /> Email Customer
          </Button>
        ) : (
          <div className="space-y-2">
            <Select value={templateKey} onValueChange={pickTemplate}>
              <SelectTrigger className="h-8 text-xs" data-testid="email-template-select">
                <SelectValue placeholder="Start from a template (optional)" />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.template_key} value={t.template_key} className="text-xs" data-testid={`email-template-opt-${t.template_key}`}>
                    {t.name || t.template_key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input value={subject} onChange={e => setSubject(e.target.value)} className="h-8 text-xs" placeholder="Subject" data-testid="email-customer-subject" />
            <textarea value={body} onChange={e => setBody(e.target.value)}
              className="w-full p-2 rounded border border-stone text-xs min-h-[110px] resize-y focus:outline-none focus:ring-1 focus:ring-ring/30 font-mono"
              placeholder="Message (HTML supported). Placeholders: {{item_title}}, {{brand}}, {{order_number}}, {{customer_name}}, {{date}}"
              data-testid="email-customer-body" />
            <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer">
              <Upload className="h-3 w-3 shrink-0" />
              <span className="truncate">{attachment ? attachment.name : 'Add an attachment (optional)'}</span>
              <input type="file" className="hidden" data-testid="email-customer-attachment"
                onChange={e => setAttachment(e.target.files?.[0] || null)} />
              {attachment && (
                <button type="button" className="text-red-400 text-[10px]" onClick={(e) => { e.preventDefault(); setAttachment(null); }}>remove</button>
              )}
            </label>
            <div className="flex gap-1.5">
              <Button size="sm" className="flex-1 text-xs gap-1.5" onClick={send} disabled={sending} data-testid="email-customer-send">
                {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Send Email
              </Button>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => setOpen(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
